<template>
  <v-card class="rounded-xl" width="100%" height="100%">
    <v-container>
      <v-row justify="center">
        <v-col cols="12">
          <v-data-table
            id="tabla"
            :headers="headers"
            :items="articles"
            item-key="item_id"
            :items-per-page="9"
            height="65vh"
            fixed-footer
            fixed-header
            show-expand
            :expanded.sync="expanded"
          >
            <!-- Encabezado y barra de herramientas -->
            <template v-slot:top>
              <v-toolbar flat id="toolbar">
                <v-toolbar-title>Items</v-toolbar-title>
                <v-divider class="mx-4" inset vertical />
                <v-spacer />
                <v-text-field
                  class="text-xs-center"
                  v-model="search"
                  append-icon="search"
                  label="Buscar"
                  single-line
                  hide-details
                />
                <v-spacer />
                <v-icon @click="$emit('close-item')">close</v-icon>
              </v-toolbar>

              <!-- Alerta informativa si es almacén padre -->
              <v-alert
                v-if="selectedWarehouseInfo?.isParent"
                type="info"
                outlined
                dense
                class="mt-2 mb-0 mx-4 warehouse-info-alert"
              >
                <div class="d-flex align-center">
                  <v-icon left color="info" small>mdi-warehouse</v-icon>
                  <div class="flex-grow-1">
                    <span class="font-weight-medium">Ubicación: {{ selectedWarehouseInfo.name }}</span>
                    <span class="ml-3 grey--text text--darken-1 caption">Haga clic en una fila para ver la distribución detallada por almacén</span>
                  </div>
                  <v-chip color="info" outlined x-small class="ml-2">
                    <v-icon left x-small>mdi-arrow-expand-vertical</v-icon>
                    Expandir para detalles
                  </v-chip>
                </div>
              </v-alert>
            </template>

            <!-- Imagen del ítem -->
            <template v-slot:[`item.item_image`]="{ item }">
              <v-avatar size="40">
                <v-img :src="item.item_image" />
              </v-avatar>
            </template>

            <!-- Selector de almacén -->
            <template v-slot:[`item.warehouse`]="{ item }">
              <v-autocomplete
                v-model="item.selectedWarehouse"
                :items="warehouseData[item.item_id] || []"
                item-text="warehouse_name"
                item-value="warehouse_id"
                :label="warehouseData[item.item_id]?.length ? 'Ubicación' : 'Sin stock'"
                outlined
                dense
                hide-details
                :disabled="!warehouseData[item.item_id]?.length"
                @change="updateItemWarehouse(item)"
              >
                <template v-slot:item="{ item: warehouse }">
                  <div>
                    <div>{{ warehouse.warehouse_name }}</div>
                    <div class="text-caption text--secondary">{{ warehouse.quantity }} disponibles</div>
                  </div>
                </template>
              </v-autocomplete>
            </template>

            <!-- Botón de agregar -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    large
                    color="primary"
                    class="mr-2"
                    v-bind="attrs"
                    v-on="on"
                    @click="selectItem(item)"
                  >add</v-icon>
                </template>
                <span>Agregar</span>
              </v-tooltip>
            </template>

            <!-- Distribución por almacén al expandir -->
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <v-card class="warehouse-distribution-card ma-3 pa-0" flat>
                  <v-card-title class="warehouse-distribution-header py-2">
                    <v-icon left>mdi-view-list</v-icon>
                    <span class="subtitle-1 font-weight-medium">Distribución por almacén</span>
                  </v-card-title>
                  <v-simple-table dense class="warehouse-distribution-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Almacén</th>
                          <th class="text-right">Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="warehouse in getItemWarehouses(item)"
                          :key="warehouse.warehouse_id"
                          class="warehouse-distribution-row"
                        >
                          <td class="text-left">
                            <div class="d-flex align-center">
                              <v-icon small class="mr-2" :color="warehouse.warehouse_id == data.warehouse ? 'primary' : 'grey'">
                                {{ warehouse.warehouse_id == data.warehouse ? 'mdi-warehouse' : 'mdi-package-variant' }}
                              </v-icon>
                              <span :class="{ 'font-weight-bold': warehouse.warehouse_id == data.warehouse }">
                                {{ warehouse.warehouse_path || warehouse.warehouse_name }}
                              </span>
                            </div>
                          </td>
                          <td class="text-right">
                            <v-chip
                              small
                              :color="warehouse.quantity > 0 ? 'success' : 'grey'"
                              :text-color="warehouse.quantity > 0 ? 'white' : ''"
                              :outlined="warehouse.quantity == 0"
                            >
                              {{ warehouse.quantity || 0 }}
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import moment from "moment";
import debounce from "lodash/debounce";

export default {
  props: {
    data: Object,
    show_warehouse: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      articles: [],
      search: "",
      expanded: [],
      warehouseData: {},
      selectedWarehouseInfo: null,
      defaultHeaders: [
        { text: "Imagen", align: "start", value: "item_image", width: "10%" },
        { text: "ID", value: "item_id" },
        { text: "Descripción", value: "item_name" },
        { text: "Cantidad", value: "quantity", width: "13%", align: "center" },
        { text: "Acciones", value: "actions", sortable: false, width: "8%" }
      ],
      warehouseHeader: { 
        text: "Ubicación", 
        value: "warehouse", 
        width: "13%", 
        align: "center", 
        sortable: false 
      }
    };
  },
  props: {
    data: {
      type: Object,
      default: () => ({ show_warehouse: false })
    }
  },
  computed: {
    headers() {
      const headers = [...this.defaultHeaders];
      
      if (this.data?.show_warehouse) {
        // Insert warehouse column before the last column (Acciones)
        headers.splice(-1, 0, this.warehouseHeader);
      }

      return headers;
    }
  },
  watch: {
    search: debounce(function (val) {
      this.fetchData(val);
    }, 400)
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData(searchTerm = "") {
      const response = await this.api_Post(
      "inventory/inventory_value_filter" ,
        {
          date: moment().format("YYYY-MM-DD"),
          business_unit_id: this.data.business,
          location_id: this.data.location,
          warehouse_id: this.data.warehouse,
          is_parent: true,
          is_now: true,
          search: searchTerm.trim()
        }
      );

      const rawItems = await response.json();
      const result = [];
      this.warehouseData = {};
      this.selectedWarehouseInfo = null;

      for (const item of rawItems) {
        let totalQty = 0;
        
        if (item.item_in_warehouses?.length) {
          this.warehouseData[item.item_id] = item.item_in_warehouses;
          
          // --- FIX 1: Correctly sum the quantity from all related warehouses ---
          // Since you request with `is_parent: true`, we should sum the quantities
          // from all warehouses returned for that item.
          totalQty = item.item_in_warehouses.reduce((sum, w) => sum + parseFloat(w.quantity || 0), 0);

          const mainWarehouse = item.item_in_warehouses.find(w => w.warehouse_id === this.data.warehouse);
          if (!this.selectedWarehouseInfo && mainWarehouse) {
            this.selectedWarehouseInfo = {
              name: mainWarehouse.warehouse_name,
              isParent: true, // We can assume this is true based on the API call
              id: mainWarehouse.warehouse_id
            };
          }
        }
        
        result.push({
          item_id: item.item_id,
          item_name: item.item_name,
          unit_name: item.unit_name,
          item_categorie_name: item.item_categorie_name,
          quantity: totalQty, // This will now be 95 initially
          price: item.price,
          item_image: this.getImage(item.image_json),
          selectedWarehouse: null // Initialize the selected warehouse model
        });
      }

      this.articles = result;
    },

    selectItem(item) {
   
      if (this.show_warehouse==true && item.selectedWarehouse==null) {
        this.$toast.warning("Por favor seleccione la ubicacion!");
       
        return;
      }

      if (item.selectedWarehouse && this.warehouseData[item.item_id]) {
        item.warehouseInfo = this.warehouseData[item.item_id].find(w => w.warehouse_id === item.selectedWarehouse);
     
      }
      if(this.show_warehouse==true)
      {
        if(item.warehouseInfo.quantity<0.0001)
      {
        this.$toast.warning("El item debe tener existencia en esta ubicacion!");
       
       return;
      }
      }

      this.$emit("selected-item", item);
    },

    updateItemWarehouse(item) {
      const selected = this.warehouseData[item.item_id]?.find(w => w.warehouse_id === item.selectedWarehouse);
      if (selected) {
        // --- FIX 2: Directly update the item's quantity to the selected warehouse's quantity ---
        // This will make the UI reactive and show the correct amount.
        item.quantity = parseFloat(selected.quantity || 0);

        this.$emit("warehouse-updated", {
          itemId: item.item_id,
          warehouseId: selected.warehouse_id,
          warehouseName: selected.warehouse_name,
          availableQuantity: selected.quantity
        });
      }
    },

    getItemWarehouses(item) {
      return this.warehouseData[item.item_id] || [];
    },

    getImage(imageJson) {
      try {
        const img = JSON.parse(imageJson);
        return img?.url || "https://via.placeholder.com/40";
      } catch {
        return "https://via.placeholder.com/40";
      }
    }
  }
};
</script>

<style scoped>
.warehouse-distribution-card {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-left: 4px solid var(--v-primary-base, #1976d2);
  border-radius: 8px;
  overflow: hidden;
}

.warehouse-distribution-header {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-bottom: 1px solid #90caf9;
  color: var(--v-primary-base, #1976d2);
}

.warehouse-distribution-table {
  background: white;
  border-radius: 0 0 8px 8px;
}

.warehouse-distribution-row {
  transition: background-color 0.3s ease;
}

.warehouse-distribution-row:hover {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%);
}
</style>
